import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  VALID_TRANSITIONS,
  EVENT_STATUS_LABELS,
  type EventStatus,
} from "@/hooks/useEvents";

interface EventStatusSelectorProps {
  currentStatus: EventStatus;
  onChange: (next: EventStatus, note?: string) => void | Promise<void>;
  disabled?: boolean;
  size?: "sm" | "default";
}

/**
 * Status dropdown that enforces the state machine: only lists valid next
 * statuses (plus the current one, disabled). Opens a confirmation dialog
 * so the user can add an optional note on the transition.
 */
export function EventStatusSelector({
  currentStatus,
  onChange,
  disabled,
  size = "default",
}: EventStatusSelectorProps) {
  const [pending, setPending] = useState<EventStatus | null>(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const validNext = VALID_TRANSITIONS[currentStatus] ?? [];

  const handleValueChange = (v: string) => {
    const next = v as EventStatus;
    if (next === currentStatus) return;
    if (!validNext.includes(next)) return;
    setPending(next);
    setNote("");
  };

  const confirm = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      await onChange(pending, note.trim() || undefined);
      setPending(null);
      setNote("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Select
        value={currentStatus}
        onValueChange={handleValueChange}
        disabled={disabled || validNext.length === 0}
      >
        <SelectTrigger className={size === "sm" ? "w-36 h-8 text-xs" : "w-44"}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={currentStatus} disabled>
            {EVENT_STATUS_LABELS[currentStatus]} (current)
          </SelectItem>
          {validNext.map((s) => (
            <SelectItem key={s} value={s}>
              {EVENT_STATUS_LABELS[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Dialog open={!!pending} onOpenChange={(v) => !v && !saving && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Change status to{" "}
              {pending ? EVENT_STATUS_LABELS[pending] : ""}?
            </DialogTitle>
            <DialogDescription>
              Transitioning from{" "}
              <strong>{EVENT_STATUS_LABELS[currentStatus]}</strong> to{" "}
              <strong>{pending ? EVENT_STATUS_LABELS[pending] : ""}</strong>.
              This will be recorded in the status history.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="transition-note">Note (optional)</Label>
            <Textarea
              id="transition-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why are you making this change? (optional)"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setPending(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={confirm} disabled={saving}>
              {saving ? "Saving..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
